/**
 * Pure, serialisable credential-format checks.
 *
 * A provider descriptor references a format by **id** (not an inline function) so
 * descriptors stay JSON-serialisable and easy to test; both the client form and
 * the server route resolve the id to the same validator here.
 */

export type FieldFormatId =
  | 'github_classic_pat'
  | 'linear_api_key'
  | 'stripe_secret_or_restricted'
  | 'datadog_api_key'
  | 'datadog_app_key'
  | 'github_owner'
  | 'none';

// A Map (not a Record) keyed by the closed FieldFormatId set — lookups go through
// `.get`, which keeps `security/detect-object-injection` satisfied.
const FORMAT_CHECKS = new Map<FieldFormatId, (value: string) => boolean>([
  // GitHub classic PAT — fine-grained (`github_pat_`), OAuth (`gho_`) and App
  // (`ghu_`/`ghs_`/`ghr_`) tokens use other prefixes and are rejected.
  ['github_classic_pat', (value): boolean => value.startsWith('ghp_')],
  // Linear personal API key.
  ['linear_api_key', (value): boolean => value.startsWith('lin_api_')],
  // Stripe secret (`sk_`) or restricted (`rk_`) key, test or live. Publishable
  // (`pk_`) keys are rejected — they grant no server-side access.
  [
    'stripe_secret_or_restricted',
    (value): boolean => /^(?:sk|rk)_(?:test|live)_[\dA-Za-z]+$/.test(value),
  ],
  // Datadog API key — 32 hex characters.
  ['datadog_api_key', (value): boolean => /^[\da-f]{32}$/i.test(value)],
  // Datadog application key — 40 hex characters.
  ['datadog_app_key', (value): boolean => /^[\da-f]{40}$/i.test(value)],
  [
    'github_owner',
    (value): boolean => {
      const characters = [...value];
      const isEdge = (character: string | undefined): boolean =>
        character !== undefined && /[\dA-Za-z]/.test(character);
      return (
        characters.length <= 39 &&
        isEdge(characters[0]) &&
        isEdge(characters.at(-1)) &&
        characters.every((character) => /[\dA-Za-z-]/.test(character))
      );
    },
  ],
  ['none', (): boolean => true],
]);

/** True when `value` matches the format identified by `id`. */
export function matchesFormat(id: FieldFormatId, value: string): boolean {
  return FORMAT_CHECKS.get(id)?.(value) ?? false;
}
